import React, { useState, useEffect, useMemo } from "react";
import { Plus, Loader2, DollarSign } from "lucide-react";
import { Plan } from "./components/dataTypes";
import PlanList from "./components/PlanList";
import PlanPagination from "./components/Pagination";
import { useNavigate } from "react-router-dom";
import { useAppServices } from "hook/services";
import { useUserInfo } from "context/user";
import Swal from "sweetalert2";
const ITEMS_PER_PAGE = 5;

const PlansDashboard: React.FC = () => {
  const AppService = useAppServices();
  const { user } = useUserInfo();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const getPlans = async () => {
    try {
      const { response, error } = await AppService.saas_plan.filter({
        query: `companyId=${user?.companyId}`,
      });
      if (response) {
        setPlans(response.data);
      }
    } catch (error) {
      setPlans([]);
    } finally {
      setIsLoading(false);
    }
  };
  let pageLoad = true;
  useEffect(() => {
    if (pageLoad) {
      getPlans();
      pageLoad = false;
    }
  }, []);

  const handleCreate = () => navigate("./configure-plan");
  const handleEdit = (id: string) => navigate(`./configure-plan/${id}`);
 const handleDelete = async (_id: string) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const { response, error } = await AppService.saas_plan.delete({
          query: `_id=${_id}`,
        });

        if (response) {
          Swal.fire("Deleted!", "The record has been deleted.", "success");
          getPlans(); 
        } else {
          Swal.fire(
            "Error!",
            error?.message || "Something went wrong.",
            "error"
          );
        }
      } catch (err: any) {
        Swal.fire("Error!", err.message || "Delete failed.", "error");
      }
    }
  };

  const totalPages = Math.ceil(plans.length / ITEMS_PER_PAGE);
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return plans.slice(start, start + ITEMS_PER_PAGE);
  }, [plans, currentPage]);

  return (
    <div className="w-full mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-8 border-b pb-4">
        <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">
          SaaS Products & Plans
        </h1>
        <button
          onClick={handleCreate}
          className="flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-lg"
        >
          <Plus className="h-5 w-5 mr-2" />
          New Plan
        </button>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-500">
          <Loader2 className="animate-spin h-8 w-8 text-indigo-500 mb-3" />
          Loading plans...
        </div>
      ) : paginated.length === 0 ? (
        <div className="text-center py-20 border rounded-xl bg-white dark:bg-gray-900">
          <DollarSign className="h-10 w-10 mx-auto mb-2 text-indigo-400" />
          <p className="text-xl font-semibold">No Plans Found</p>
          <p className="text-sm mt-1">Create your first SaaS plan to begin.</p>
        </div>
      ) : (
        <>
          <PlanList
            plans={paginated}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
          <PlanPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={plans.length}
            onPageChange={setCurrentPage}
            itemsPerPage={ITEMS_PER_PAGE}
          />
        </>
      )}
    </div>
  );
};

export default PlansDashboard;
